import PropTypes from 'prop-types'
import React from 'react'

import HowToBlock from 'components/_special/HowToBlock'
import ButtonRounded from 'components/ButtonRounded'
import SliderFrameMacStraight from 'components/SliderFrameMacStraight'
import { Text } from 'styles/components/typography'
import { blackOp07 } from 'styles/theme/colors'

import HowToResultsTablet from './components/HowToResultsTablet'

/**
 * HowToResults - result component to render "Result" block on
 * the "How it works" pages for all breakpoints
 *
 * @param {object} props - properties of component
 * @returns {node} HowToResults
 */
const HowToResults = ({
  viewport,
  pathCdn,
  data,
  index,
  buttonText,
  title,
  link,
  asPath,
  content,
  linkAs,
  isSquareHidden,
  squareColor,
  isWhiteArrow,
  lineColor,
  textColor,
  buttonColor,
  separatorColor,
  isLast,
}) => {
  const descrArray = React.useMemo(() => data.map(({ title }) => title), [data])
  const images = React.useMemo(() => data.map(({ image }) => image), [data])
  const { mobile, tablet } = viewport
  const Button = (
    <ButtonRounded
      as={linkAs}
      to={link}
      asPath={asPath}
      bgClassName={`bg--${buttonColor}`}
    >
      {buttonText}
    </ButtonRounded>
  )
  return (
    <>
      <HowToBlock
        isSquareHidden={isSquareHidden}
        index={index}
        title={title}
        titleContent={
          !mobile ? (
            <div className="d-inline-block pl-md-2">{Button}</div>
          ) : null
        }
        squareColor={squareColor}
        isLast={isLast}
        lineColor={lineColor}
        textColor={textColor}
      >
        {mobile && <Text dangerouslySetInnerHTML={{ __html: content }} />}

        {!mobile && (
          <HowToResultsTablet
            data={data}
            pathCdn={pathCdn}
            content={content}
            button={tablet ? Button : null}
            isWhiteArrow={isWhiteArrow}
            {...{ separatorColor, textColor }}
            isLast={isLast}
          />
        )}
      </HowToBlock>

      {mobile && (
        <>
          <SliderFrameMacStraight
            images={images}
            staticPath={pathCdn}
            descrArray={descrArray}
            index={index}
            isLightColor={isWhiteArrow}
          />
          <div className="text-center">{Button}</div>
        </>
      )}
    </>
  )
}

HowToResults.propTypes = {
  /** Text for button */
  buttonText: PropTypes.string.isRequired,

  /** Description for block */
  content: PropTypes.string.isRequired,

  /** Array with descriptions, icons and images  */
  data: PropTypes.array.isRequired,

  /** Index of step block on a page */
  index: PropTypes.number.isRequired,

  /** Link for button */
  link: PropTypes.string.isRequired,

  /** Route as path */
  asPath: PropTypes.string.isRequired,

  /** Path to a directory with page's files */
  pathCdn: PropTypes.string.isRequired,

  /** Title of block */
  title: PropTypes.string.isRequired,

  /** Object with flags of breakpoints */
  viewport: PropTypes.object.isRequired,

  /** Square background color */
  squareColor: PropTypes.string.isRequired,

  /** Flag to control square visibility */
  isSquareHidden: PropTypes.bool,

  /** Type of link component (use it inside styleguide as 'a') */
  linkAs: PropTypes.string,

  /** Flag to set white color to arrow */
  isWhiteArrow: PropTypes.bool,

  /** Line background color */
  lineColor: PropTypes.oneOf(['light', 'dark']),

  /** Text color */
  textColor: PropTypes.oneOf(['light', 'dark']),

  /** Button background color */
  buttonColor: PropTypes.string,

  /** Separator color */
  separatorColor: PropTypes.string,

  isLast: PropTypes.bool,
}

HowToResults.defaultProps = {
  linkAs: 'Link',
  isSquareHidden: false,
  lineColor: 'dark',
  textColor: 'dark',
  buttonColor: 'orange',
  separatorColor: blackOp07,
  isLast: false,
}

export default HowToResults
